import setValueFromUrl from './_setValueFromUrl';
import checkValue from './_checkValue';
import getValidDate from './../../utils/dates/getValidDate';
import getDefaultMember from '../storage-manager/getDefaultMember';
import memberType from '../../core/memberType';
import parseHeight from '../helpers/parseHeight';

let countChild = 0;

/**
 * Function to filter undefined values before Object.assign
 * @param {Object} target the default object
 * @param  {...any} sources objects that will be
 * @returns {Object}
 */
function assign(target, ...sources) {
  return Object.assign(target, ...sources.map(x =>
    Object.entries(x)
      .filter(([, value]) => value !== undefined)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {}),
  ));
}

/**
 * @param {String} gender gender
 * @returns {String|undefined}
 */
function fixGender(gender = '') {
  const character = gender.toLowerCase()[0];

  return character === 'm' || character === 'f'
    ? character
    : undefined;
}

/**
 * @param {Object} urlParams urlParams
 * @param {String} suffix suffix
 * @param {String} type member type
 * @returns {undefined|Object}
 */
function getMember(urlParams, suffix, type) {
  let member = {};

  setValueFromUrl(
    [`fname${suffix}`, ...(type === memberType.primary ? ['first_name', 'fn1', 'firstName', 'firstName-applicant'] : [])],
    urlParams, { firstName: member });

  setValueFromUrl(
    [`lname${suffix}`, ...(type === memberType.primary ? ['last_name', 'ln1', 'lastName', 'lastName-applicant'] : [])],
    urlParams, { lastName: member });

  setValueFromUrl(
    [`tobacco${suffix}`, ...(type === memberType.primary ? ['tobaccoUse', 'tob1', 'applicant[applicant][tobacco]'] : [])],
    urlParams, { tobacco: member });

  setValueFromUrl(
    [`gender${suffix}`, ...(type === memberType.primary ? ['gnd1', 'applicant[applicant][gender]', 'gender-applicant'] : [])],
    urlParams, { gender: member });
  member.gender = fixGender(member.gender);

  setValueFromUrl(
    [`dob${suffix}`, ...(type === memberType.primary ? ['dob1', 'applicant[applicant][dateOfBirth]', 'date_of_birth', 'dob-applicant'] : [])],
    urlParams, { dob: member });

  setValueFromUrl(
    [`height${suffix}`],
    urlParams, { height: member });

  setValueFromUrl(
    [`weight${suffix}`],
    urlParams, { weight: member });

  if (member.dob) {
    member.dob = getValidDate(member.dob);
  }

  if (member.height) {
    member.height = parseHeight(member.height);
  }

  let hastValue = false;
  for (const key in member) {
    if (typeof member[key] !== 'undefined') {
      hastValue = true;
    }
  }

  if (hastValue) {
    member = assign(getDefaultMember(), member);
    delete member.email;
    delete member.address;
    delete member.phone;
    delete member.phoneIsMobile;

    member.type = type;
    member.id = type.toLowerCase();

    if (type === memberType.child) {
      countChild += 1;
      member.id = `${member.id}_${countChild}`;
    }

    return member;
  }

  return undefined;
}

/**
 * @param {Object} urlParams urlParams
 * @returns {undefined|Array}
 */
function getMembersFromURL(urlParams) {
  let members = [
    getMember(urlParams, '', memberType.primary),
    getMember(urlParams, '_s', memberType.spouse),
  ];

  let index = 0;
  for (; index < 11; index += 1) {
    members.push(
      getMember(urlParams, `_${index}`, memberType.child),
    );
  }

  members = members.filter(Boolean);

  return members.length > 0
    ? members
    : undefined;
}

/**
 * Updates the `hc` object with values from the URL.
 *
 * @param {Object} urlParams - all search parameters in the current URL
 * @param {Object} hcStorage - the `hc` object to be updated
 */
export default function updateValuesFromUrl(urlParams, hcStorage) {
  if (!hcStorage.members.length) {
    hcStorage.members.push(getDefaultMember());
  }

  hcStorage.members = getMembersFromURL(urlParams) || hcStorage.members;

  const applicant = hcStorage.members[0];
  if (urlParams.address) {
    hcStorage.contact.address = checkValue(hcStorage.contact.address, urlParams.address).value.replace(/[+]/g, ' ');
    applicant.address = checkValue(applicant.address, urlParams.address).value.replace(/[+]/g, ' ');
  }

  const universalID = document.getElementById('leadid_token');
  hcStorage.leadid.universal_leadid = universalID ? universalID.value : '';

  setValueFromUrl(
    ['email_address', 'email', 'e'],
    urlParams, { email_address: hcStorage.contact, email: applicant });

  setValueFromUrl(
    ['phone'],
    urlParams, { phone_home: hcStorage.contact, phone: applicant });

  setValueFromUrl(
    ['phone_ismobile', 'mobile'],
    urlParams, { is_mobile: hcStorage.contact, phoneIsMobile: applicant });

  setValueFromUrl(['age1'], urlParams, { age: applicant });

  setValueFromUrl(
    ['income', 'applicant[houseHoldIncome]'],
    urlParams, { income: hcStorage.household });

  setValueFromUrl(
    ['size', 'applicant[houseHoldSize]'],
    urlParams, { size: hcStorage.household });

  setValueFromUrl(
    ['sdate'],
    urlParams, { sdate: hcStorage });

  setValueFromUrl(
    ['period'],
    urlParams, { period: hcStorage });

  if (hcStorage.members.length > hcStorage.household.size) {
    hcStorage.household.size = hcStorage.members.length;
  }

  setValueFromUrl(['qle'], urlParams, { life_event: hcStorage });
}
